import { CellValueChangedEvent } from "ag-grid-community";
import { useCallback } from "react";
import { showWarning } from "src/utils/toast/toastUtil";
import {
	KSK1110_R01DetailDto,
	KSK1110_R01InspectionValueField,
	KSK1110_R01_VALUE_FIELDS,
} from "../Dto_KSK1110_R01";
import {
	normalizeKSK1110_R01InspectionValue,
	normalizeKSK1110_R01ValueType,
	validateKSK1110_R01InspectionValue,
} from "../Util_KSK1110_R01";

interface UseKSK1110_R01DetailCellChangeProps {
	detailList: KSK1110_R01DetailDto[];
	updateKSK1110_R01DetailList: (valueIndex: number, data: KSK1110_R01DetailDto) => void;
}

export const useKSK1110_R01DetailCellChange = ({
	detailList,
	updateKSK1110_R01DetailList,
}: UseKSK1110_R01DetailCellChangeProps) => {
	const handleCellValueChanged = useCallback((event: CellValueChangedEvent<KSK1110_R01DetailDto>) => {
		if (!event.data || event.node.rowPinned === "bottom") return;

		const field = event.colDef.field;
		if (field === "remark") {
			const remark = String(event.newValue ?? "").trim();
			if (remark.length > 1000) {
				showWarning("비고는 1000자 이내로 입력해 주세요.");
				const previousValue = String(event.oldValue ?? "");
				event.node.setDataValue(field, previousValue);
				updateKSK1110_R01DetailList(event.data.valueIndex, {
					...event.data,
					remark: previousValue,
				});
				return;
			}
			updateKSK1110_R01DetailList(event.data.valueIndex, {
				...event.data,
				remark,
			});
			return;
		}
		if (!KSK1110_R01_VALUE_FIELDS.includes(field as KSK1110_R01InspectionValueField)) return;
		const valueField = field as KSK1110_R01InspectionValueField;

		const normalizedValue = normalizeKSK1110_R01InspectionValue(
			event.data.valueType,
			event.newValue,
		);
		const updatedDetail = { ...event.data, [valueField]: normalizedValue };
		if (!validateKSK1110_R01InspectionValue(updatedDetail)) {
			showWarning(
				normalizeKSK1110_R01ValueType(event.data.valueType) === "NUMERIC"
					? "숫자 형식으로 입력해 주세요."
					: "검사 결과 형식을 확인해 주세요.",
			);
			const previousValue = String(event.oldValue ?? "");
			event.node.setDataValue(valueField, previousValue);
			updateKSK1110_R01DetailList(event.data.valueIndex, {
				...event.data,
				[valueField]: previousValue,
			});
			return;
		}

		updateKSK1110_R01DetailList(event.data.valueIndex, updatedDetail);
	}, [updateKSK1110_R01DetailList]);

	const handlePassFailChange = useCallback((
		valueIndex: number,
		valueField: KSK1110_R01InspectionValueField,
		value: "Y" | "N" | "",
	) => {
		const detail = detailList.find((item) => item.valueIndex === valueIndex);
		if (!detail || normalizeKSK1110_R01ValueType(detail.valueType) !== "PASS_FAIL") return;
		updateKSK1110_R01DetailList(valueIndex, {
			...detail,
			[valueField]: value,
		});
	}, [detailList, updateKSK1110_R01DetailList]);

	return { handleCellValueChanged, handlePassFailChange };
};
